
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

interface AdminAuthProps {
  children: React.ReactNode;
}

const AdminAuth = ({ children }: AdminAuthProps) => {
  const { isLoading, user, role } = useAuth();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    console.log('AdminAuth check - Loading:', isLoading, 'User:', !!user, 'Role:', role);
    
    if (!isLoading) {
      if (!user) {
        console.log('No user found, redirecting to auth page');
        navigate('/auth');
      } else if (role !== 'admin') {
        console.log('User is not admin, setting error and will redirect');
        setAuthError('You do not have administrator privileges');
        setTimeout(() => navigate('/auth'), 3000);
      } else {
        console.log('Admin authentication successful');
        setAuthError(null);
      }
    }
  }, [isLoading, user, role, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-6 max-w-md">
          <div className="flex items-center mb-4">
            <AlertCircle className="h-6 w-6 mr-2" />
            <h2 className="text-lg font-semibold">Authentication Required</h2>
          </div>
          <p>You need to sign in to access the admin area.</p>
          <p className="mt-2">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-6 max-w-md">
          <div className="flex items-center mb-4">
            <AlertCircle className="h-6 w-6 mr-2" />
            <h2 className="text-lg font-semibold">Access Restricted</h2>
          </div>
          <p>{authError}</p>
          <p className="mt-2">This area is restricted to admin users only.</p>
          <p className="mt-2">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Only render children if user is authenticated and is an admin
  return <>{children}</>;
};

export default AdminAuth;
